/**
 * GuestSaveReminderModal.jsx — Rappel unique pour l'élève invité (sans compte)
 * quand il atteint 5 fiches acquis dans le livret.
 *
 * Affichage :
 *  - Modal centré, doux, pas bloquant (fermable)
 *  - Se rappelle qu'il a été montré une fois (localStorage)
 *  - Deux CTA : "Créer un compte" (préserve `?next=`) + "Continuer sans compte"
 *
 * Usage :
 *  <GuestSaveReminderModal
 *    open={showReminder}
 *    onClose={() => setShowReminder(false)}
 *    livretPath="/livrets/parcours-am"
 *    livretName="Livret pédagogique Permis AM"
 *    acquis={5}
 *    total={36}
 *  />
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { X, Save, Sparkles, Cloud, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';

export default function GuestSaveReminderModal({
  open,
  onClose,
  livretPath,
  livretName = 'Livret pédagogique',
  acquis = 0,
  total = 0,
  accentColor = '#10b981',
}) {
  if (!open) return null;
  const nextUrl = livretPath || '/';
  const encodedNext = encodeURIComponent(nextUrl);

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="guest-save-reminder"
    >
      <div
        className="relative w-full max-w-md bg-gradient-to-br from-slate-900 via-slate-950 to-black border-2 rounded-3xl overflow-hidden shadow-2xl"
        style={{
          borderColor: `${accentColor}55`,
          boxShadow: `0 24px 60px ${accentColor}33, 0 0 0 1px ${accentColor}22`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center transition"
          aria-label="Fermer"
          data-testid="guest-reminder-close"
        >
          <X size={18} />
        </button>

        {/* Confettis discrets */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-60">
          {[...Array(14)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1.5 h-3 rounded-sm"
              style={{
                left: `${(i * 7.5) % 100}%`,
                top: `${5 + ((i * 11) % 90)}%`,
                background: ['#fbbf24', '#10b981', '#22d3ee', '#f43f5e', '#facc15'][i % 5],
                transform: `rotate(${(i * 37) % 360}deg)`,
                opacity: 0.55,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 p-6 sm:p-7">
          {/* Header */}
          <div className="text-center mb-5">
            <div
              className="inline-flex items-center gap-1.5 w-16 h-16 rounded-2xl mx-auto mb-3 justify-center"
              style={{
                background: `${accentColor}22`,
                border: `1.5px solid ${accentColor}66`,
              }}
            >
              <Save className="w-8 h-8" style={{ color: accentColor }} />
            </div>
            <div
              className="text-[10px] font-black uppercase tracking-[0.22em] mb-1"
              style={{ color: accentColor }}
            >
              🎉 Belle progression ! · {acquis}/{total} acquis
            </div>
            <h2
              className="text-2xl sm:text-3xl font-black text-white leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Enregistre ta progression
            </h2>
            <p className="text-sm text-white/70 mt-2 leading-snug">
              Tu as déjà validé <strong className="text-white">{acquis} fiches</strong> de ton livret.
              Crée un compte gratuit pour ne pas les perdre et retrouver ton livret sur tous tes appareils.
            </p>
          </div>

          {/* Bénéfices */}
          <ul className="space-y-2 mb-6">
            <li className="flex items-center gap-3 text-[13px] text-white/85">
              <Cloud className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />
              <span>Ta progression est sauvegardée automatiquement dans le cloud</span>
            </li>
            <li className="flex items-center gap-3 text-[13px] text-white/85">
              <ShieldCheck className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />
              <span>Tu retrouves ton livret sur mobile, tablette et ordinateur</span>
            </li>
            <li className="flex items-center gap-3 text-[13px] text-white/85">
              <Sparkles className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />
              <span>Ton enseignant peut suivre où tu en es</span>
            </li>
          </ul>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              asChild
              size="lg"
              className="flex-1 text-white font-black uppercase tracking-widest shadow-lg"
              style={{ background: accentColor }}
              data-testid="guest-reminder-signup"
            >
              <Link to={`/register?next=${encodedNext}`}>
                <Sparkles className="w-4 h-4 mr-1.5" /> Créer un compte gratuit
              </Link>
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="border-white/25 text-white/80 hover:bg-white/5"
              data-testid="guest-reminder-later"
            >
              Plus tard
            </Button>
          </div>

          <p className="mt-4 text-center text-[10.5px] text-white/40 leading-relaxed">
            30 secondes chrono · sans e-mail obligatoire pour un compte élève
          </p>
        </div>
      </div>
    </div>
  );
}
